import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { AuthAPI, extractError, tokens } from "@/lib/api";
import { toast } from "sonner";

export default function RegisterPage() {
    const { t } = useT();
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        workshop_name: "",
        owner_name: "",
        owner_email: "",
        owner_password: "",
        branch_code: "",
        branch_name: "",
        branch_state_code: "KA",
        currency: "INR",
        locale: "en",
    });

    function set(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function onSubmit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await AuthAPI.registerTenant(form);
            tokens.set(res);
            await refresh();
            toast.success("Workshop created");
            nav("/", { replace: true });
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-5" data-testid="register-page">
            <div className="col-span-1 flex flex-col px-6 py-10 lg:col-span-3 lg:px-12">
                <div className="flex items-center gap-2">
                    <span className="grid h-8 w-8 place-items-center rounded-md bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
                        <Wrench className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                    <span className="font-display text-base font-semibold tracking-tight">
                        {t("brand.full")}
                    </span>
                </div>

                <div className="mx-auto my-auto w-full max-w-3xl py-12">
                    <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        {t("register.title")}
                    </h1>
                    <p className="mt-2 text-sm text-zinc-500">{t("register.subtitle")}</p>

                    <Card className="mt-8 border-zinc-200 bg-white p-6 shadow-none dark:border-zinc-800 dark:bg-zinc-900">
                        <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit}>
                            <Field label={t("register.workshop_name")} testid="reg-workshop">
                                <Input
                                    required
                                    value={form.workshop_name}
                                    onChange={(e) => set("workshop_name", e.target.value)}
                                    data-testid="reg-workshop-input"
                                />
                            </Field>
                            <Field label={t("register.owner_name")} testid="reg-name">
                                <Input
                                    required
                                    value={form.owner_name}
                                    onChange={(e) => set("owner_name", e.target.value)}
                                    data-testid="reg-name-input"
                                />
                            </Field>
                            <Field label={t("register.owner_email")} testid="reg-email">
                                <Input
                                    required
                                    type="email"
                                    value={form.owner_email}
                                    onChange={(e) => set("owner_email", e.target.value)}
                                    data-testid="reg-email-input"
                                />
                            </Field>
                            <Field label={t("register.owner_password")} testid="reg-password">
                                <Input
                                    required
                                    type="password"
                                    value={form.owner_password}
                                    onChange={(e) => set("owner_password", e.target.value)}
                                    placeholder="Min 10 chars, letters + digits"
                                    data-testid="reg-password-input"
                                />
                            </Field>
                            <Field label={t("register.branch_code")} testid="reg-bcode">
                                <Input
                                    required
                                    value={form.branch_code}
                                    onChange={(e) => set("branch_code", e.target.value.toUpperCase())}
                                    placeholder="e.g. HQ-01"
                                    className="font-mono uppercase"
                                    data-testid="reg-bcode-input"
                                />
                            </Field>
                            <Field label={t("register.branch_name")} testid="reg-bname">
                                <Input
                                    required
                                    value={form.branch_name}
                                    onChange={(e) => set("branch_name", e.target.value)}
                                    data-testid="reg-bname-input"
                                />
                            </Field>
                            <Field label={t("register.branch_state")} testid="reg-state">
                                <Input
                                    required
                                    value={form.branch_state_code}
                                    onChange={(e) =>
                                        set("branch_state_code", e.target.value.toUpperCase())
                                    }
                                    className="font-mono uppercase"
                                    data-testid="reg-state-input"
                                />
                            </Field>
                            <Field label={t("register.currency")} testid="reg-currency">
                                <Input
                                    required
                                    value={form.currency}
                                    onChange={(e) => set("currency", e.target.value.toUpperCase())}
                                    className="font-mono uppercase"
                                    data-testid="reg-currency-input"
                                />
                            </Field>

                            <div className="md:col-span-2 mt-2 flex items-center justify-between">
                                <Link
                                    to="/login"
                                    className="text-sm text-zinc-500 hover:underline"
                                    data-testid="link-login"
                                >
                                    ← {t("register.have_account")}
                                </Link>
                                <Button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="reg-submit"
                                >
                                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    {t("register.submit")}
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>
            </div>

            <div className="relative hidden bg-zinc-900 lg:col-span-2 lg:block">
                <img
                    alt="Workshop"
                    src="https://images.pexels.com/photos/6870309/pexels-photo-6870309.jpeg"
                    className="absolute inset-0 h-full w-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-bl from-zinc-950/80 via-zinc-950/30 to-zinc-950/10" />
            </div>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <div className="space-y-1.5" data-testid={testid}>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                {label}
            </Label>
            {children}
        </div>
    );
}
